import filesize from 'filesize'
import React, {useEffect, useState} from 'react'
import {useSelector} from 'react-redux'

import {Button} from '@ua/design-system/components/button'
import {Input} from '@ua/design-system/components/input'
import {Radio, RadioGroup} from '@ua/design-system/components/radio'
import {Select} from '@ua/design-system/components/select'
import {Stack} from '@ua/design-system/components/stack'
import {Text} from '@ua/design-system/components/text'
import {Textarea} from '@ua/design-system/components/textarea'

import {FileUpload} from 'airship/components/FileUpload'
import {FieldValidation, ValidationScope} from 'airship/components/Validator'
import {isRequired} from 'airship/components/Validator/validators'
import Feedback from 'airship/patterns/feedback'
import fileToDataURI from 'airship/utils/fileToDataURI'

import AdditionalFields from '../AdditionalFields/AdditionalFields'
import {
  DEFAULT_IMAGE_DETAILS,
  IMAGE_LIMIT_SIZE,
  PRODUCTS_WITH_ADDITIONAL_FIELDS,
  UPLOAD_IMAGE_SIZE_ERROR_MESSAGE,
  URGENCY_SELECTOR_ITEMS,
} from '../constants'
import {productsSelect} from '../products'
import {selectErrorMessage, selectIsUnauthorized} from '../selectors'
import {ContactSupportDetails, ImageDetails, Urgency} from '../types'
import ContactSupportAccessDeniedError from './ContactSupportAccessDeniedError'
import styles from './contactSupportModal.module.css'
import Details from './details'
import EditDetails from './editDetails'

type Props = {
  handleIsValid: (isValid: boolean) => void
  details: ContactSupportDetails
  onChange: (
    key: keyof ContactSupportDetails,
    value: string | ImageDetails
  ) => void
  onReset: () => void
}

const ContactSupportModalContent = ({
  handleIsValid,
  details,
  onChange: handleChange,
  onReset,
}: Props): JSX.Element => {
  const [fieldError, setFieldError] = useState<string>('')
  const errorMessage = useSelector(selectErrorMessage)
  const isUnauthorized = useSelector(selectIsUnauthorized)
  const [showEditDetails, setShowEditDetails] = useState<boolean>(false)

  const handleReset = (): void => {
    setShowEditDetails(false)
    onReset()
  }

  useEffect(() => {
    const handleCloseModal = (): void => {
      setShowEditDetails(false)
      handleReset()
    }

    return () => {
      handleCloseModal()
    }
  }, [])

  const onFileRemove = (): void => {
    handleChange('image', DEFAULT_IMAGE_DETAILS)
    if (fieldError === UPLOAD_IMAGE_SIZE_ERROR_MESSAGE) {
      setFieldError('')
    }
  }

  const onFileSelect = async (
    ev: React.ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    if (!ev.target.files) return

    const files: FileList = ev.target.files
    const file = files[0]

    if (!file) return
    const newImageData = await fileToDataURI(file)
    handleChange('image', {data: newImageData, name: file.name})

    if (file.size > IMAGE_LIMIT_SIZE) {
      setFieldError(UPLOAD_IMAGE_SIZE_ERROR_MESSAGE)
    }
  }

  return (
    <div>
      {showEditDetails ? (
        <EditDetails setShowEditDetails={setShowEditDetails} />
      ) : (
        <Details setShowEditDetails={setShowEditDetails} />
      )}

      <ValidationScope onValid={handleIsValid}>
        <Stack>
          <FieldValidation
            id="product"
            value={details.product}
            validators={[isRequired()]}
            asyncValidators={[]}
          >
            <Select
              id="product"
              label="Where is your issue?"
              placeholder="Select product or feature"
              isRequired
              onChange={(e) => handleChange('product', e.target.value)}
            >
              {productsSelect.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FieldValidation>

          {details.product !== '' &&
          PRODUCTS_WITH_ADDITIONAL_FIELDS.includes(details.product) ? (
            <AdditionalFields selected={details.product} />
          ) : null}

          <FieldValidation
            id="subject"
            value={details.subject}
            validators={[isRequired()]}
            asyncValidators={[]}
          >
            <Input
              label="Subject"
              placeholder="Briefly describe your issue"
              value={details.subject ?? ''}
              onChange={(e) => handleChange('subject', e.target.value)}
              isRequired
            />
          </FieldValidation>

          <FieldValidation
            id="issueDescription"
            value={details.issueDescription}
            validators={[isRequired()]}
            asyncValidators={[]}
          >
            <Textarea
              label="Issue"
              placeholder="Describe your issue in detail"
              value={details.issueDescription}
              onChange={(e) => handleChange('issueDescription', e.target.value)}
              size="lg"
              isRequired
            />
          </FieldValidation>
          <Stack spacing="8px">
            <Text variant="bodyLarge">Attach screenshot</Text>
            <Text variant="bodySmall">
              Image must be a .jpg, .gif or .png file type with a size limit of{' '}
              {filesize(IMAGE_LIMIT_SIZE)}.
            </Text>

            {!details.image.data && (
              <div>
                <FileUpload
                  accept="image/jpeg,image/png,image/gif"
                  onChange={onFileSelect}
                  buttonText="Choose file"
                />
              </div>
            )}
            {details.image.data && (
              <div className="u-flex u-space-bottom-1">
                <img
                  className={styles.ipsThumbnail}
                  src={details.image.data}
                  alt=""
                />
                <Button size="sm" onClick={() => onFileRemove()}>
                  remove
                </Button>
              </div>
            )}
          </Stack>

          <Stack spacing="8px">
            <Text variant="bodyLarge">Urgency</Text>
            <RadioGroup
              name="storybook-radio-group"
              onChange={(item: Urgency) => handleChange('urgency', item)}
              value={details.urgency}
              direction="row"
            >
              {URGENCY_SELECTOR_ITEMS.map((item) => (
                <Radio key={item.id} value={item.id}>
                  {item.label}
                </Radio>
              ))}
            </RadioGroup>
          </Stack>
        </Stack>
      </ValidationScope>
      {errorMessage || fieldError ? (
        <Feedback displayClasses="u-space-bottom-1" type="error">
          {errorMessage ?? fieldError}
        </Feedback>
      ) : null}

      {isUnauthorized ? <ContactSupportAccessDeniedError /> : null}
    </div>
  )
}

export default ContactSupportModalContent
